import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Phone } from "lucide-react";
import { Input } from "../components/ui/input";
import { Chips } from "../components/Chips";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getAttribution, trackLead, trackSchedule } from "../lib/tracking";
import { TREATMENT_OPTIONS } from "../data/services";
import { BOOKING_URL, CHERRY_URL, DEPOSIT, IMG, PHONE_DISPLAY, PHONE_TEL, ADDRESS_LINE1, ADDRESS_LINE2 } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under $500", "$500–$1,500", "$1,500–$5,000", "$5,000+"];
const PLANS = ["Package of 3", "Single treatment", "Not sure yet"];
const PRICES = [
  ["Skin Consultation", "Complimentary"],
  ["Medical Consultation", "$75"],
  ["Botox® Cosmetic", "Starting at $15/unit (100+ units $10/unit)"],
  ["Xeomin®", "Starting at $11/unit"],
  ["Juvéderm® Dermal Fillers", "Starting at $650/syringe"],
  ["Hydration Facial", "$200"],
  ["Hollywood Carbon Facial", "$200"],
  ["VI Peel®", "$349"],
  ["Eyebrow Enhancement", "$650"],
  ["RF Microneedling", "Starting at $699"],
  ["Skin Reset — Face, Neck & Chest", "$1,000 · 3 for $2,400"],
  ["Cosmelan® Chemical Peel", "$1,000"],
  ["Xerf Skin Tightening", "Starting at $2,800"],
  ["Erbium Laser Resurfacing", "Starting at $2,500"],
  ["CO₂ Laser Resurfacing", "Starting at $3,000"],
  ["Body Contouring (CyroSlimming + EMS)", "Starting at $399"],
  ["Medical Weight Loss Programs", "Starting at $299"],
];
const field = "h-12 rounded-none border-0 border-b border-neutral-300 bg-transparent px-0 text-base font-light shadow-none focus-visible:border-neutral-950 focus-visible:ring-0";
const lbl = "text-[10px] uppercase tracking-[0.25em] text-neutral-500";

export const MiniHeader = () => (
  <header className="border-b border-neutral-200 bg-white" data-testid="landing-header">
    <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4 md:px-8">
      <Link to="/" data-testid="landing-logo-link"><img src="/logo-black.png" alt="Lux Veritae Skin Spa" className="h-16 w-auto md:h-20" /></Link>
      <div className="flex items-center gap-6 text-[10px] uppercase tracking-[0.25em] text-neutral-700">
        <a href={`tel:${PHONE_TEL}`} className="hidden items-center gap-2 hover:text-neutral-950 sm:flex" data-testid="landing-call-link"><Phone size={13} strokeWidth={1.25} /> {PHONE_DISPLAY}</a>
        <Link to="/" className="link-under" data-testid="landing-website-link">Home</Link>
      </div>
    </div>
  </header>
);

const NextStep = ({ name, offer }) => (
  <div className="rise" data-testid="landing-next-step">
    <p className="kicker">Request received</p>
    <h2 className="font-display mt-3 text-4xl font-light text-neutral-950">Thank you, {name.split(" ")[0]}.</h2>
    <p className="mt-4 text-base font-light leading-relaxed text-neutral-600">Your details are with our concierge. {offer.badge ? `Your ${offer.badge.toLowerCase()} is noted. ` : ""}Reserve your time now — a {DEPOSIT} deposit secures your appointment and is collected safely when you book online.</p>
    <div className="mt-8 flex flex-col gap-3">
      <a href={BOOKING_URL} target="_blank" rel="noreferrer" onClick={trackSchedule} className="btn-ink" data-testid="landing-book-deposit-btn">Book Appointment · {DEPOSIT} Deposit</a>
      <a href={BOOKING_URL} target="_blank" rel="noreferrer" onClick={trackSchedule} className="btn-line" data-testid="landing-book-consult-btn">Schedule Complimentary Consultation</a>
    </div>
    <p className="mt-6 text-xs font-light text-neutral-500">Prefer to talk first? <a href={`tel:${PHONE_TEL}`} className="underline" data-testid="landing-next-call">Call</a> or <a href={`sms:${PHONE_TEL}`} className="underline" data-testid="landing-next-text">text</a> {PHONE_DISPLAY}.</p>
  </div>
);

const LeadForm = ({ onDone, offer }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", treatment: "", budget: "", package_interest: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.treatment) return toast.error("Please choose the treatment you're interested in.");
    if (!f.budget) return toast.error("Please select your investment range.");
    if (!f.package_interest) return toast.error("Please choose a package or single treatment.");
    setSending(true);
    try {
      const attr = getAttribution();
      await axios.post(`${API}/leads`, { ...f, ...attr, page: offer.page, source: attr.utm_source || "website" });
      trackLead({ content_name: f.treatment, content_category: offer.page });
      onDone(f.name);
    } catch {
      toast.error("We couldn't send your details. Please check them and try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-7" data-testid="lead-form">
      <p className="kicker">Tell us about you</p>
      <label className="block"><span className={lbl}>Full Name</span><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="lead-name-input" /></label>
      <label className="block"><span className={lbl}>Email</span><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="lead-email-input" /></label>
      <label className="block"><span className={lbl}>Phone Number</span><Input required type="tel" inputMode="tel" autoComplete="tel" placeholder="(602) 555-0123" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={field} data-testid="lead-phone-input" /></label>
      <div><span className={lbl}>Treatment You Want</span>
        <Select value={f.treatment} onValueChange={(v) => setF({ ...f, treatment: v })}>
          <SelectTrigger className={`${field} w-full`} data-testid="lead-treatment-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent className="rounded-none">{TREATMENT_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><span className={lbl}>Investment Range</span>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {BUDGETS.map((b) => (
            <button type="button" key={b} onClick={() => setF({ ...f, budget: b })} data-testid={`lead-budget-${BUDGETS.indexOf(b)}`}
              className={`border px-3 py-3 text-xs tracking-[0.08em] transition-colors ${f.budget === b ? "border-neutral-950 bg-neutral-950 text-white" : "border-neutral-300 text-neutral-800 hover:border-neutral-950"}`}>{b}</button>
          ))}
        </div></div>
      <div><span className={lbl}>Package or Single Treatment?</span><Chips options={PLANS} value={f.package_interest} onChange={(v) => setF({ ...f, package_interest: v })} testid="lead-plan" cols="grid-cols-3" /></div>
      <button type="submit" disabled={sending} className="btn-ink w-full disabled:opacity-60" data-testid="lead-submit-btn">{sending ? "Sending…" : "Submit My Request"}</button>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.25em] text-neutral-400"><span className="h-px flex-1 bg-neutral-200" />or<span className="h-px flex-1 bg-neutral-200" /></div>
      <a href={BOOKING_URL} target="_blank" rel="noreferrer" onClick={trackSchedule} className="btn-line w-full" data-testid="lead-book-direct-btn">Already decided? Book Directly</a>
    </form>
  );
};

const CherryNote = () => (
  <div className="flex flex-col gap-5 border border-neutral-950 bg-[hsl(var(--stone))] p-6 sm:flex-row sm:items-center sm:justify-between md:p-8" data-testid="landing-cherry-card">
    <div>
      <p className="kicker">Pay Over Time with Cherry</p>
      <p className="font-display mt-2 text-2xl text-neutral-950">3 minutes to apply. No hard credit check.</p>
      <p className="mt-2 text-sm font-light text-neutral-600">0% APR options may be available.</p>
    </div>
    <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="btn-ink shrink-0" data-testid="landing-cherry-apply-btn">Apply for Cherry Financing</a>
  </div>
);

export default function Landing({ offer }) {
  const [doneName, setDoneName] = useState("");
  return (
    <div className="bg-white" data-testid="landing-page">
      <MiniHeader />
      <div className="mx-auto max-w-[1200px] px-5 pt-8 md:px-8 md:pt-10"><CherryNote /></div>
      <section className="mx-auto grid max-w-[1200px] gap-16 px-5 py-12 md:px-8 md:py-16 lg:grid-cols-[1.1fr_1fr] lg:gap-24">
        <div>
          {offer.badge && <span className="rise mb-6 inline-block border border-neutral-950 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-neutral-950" data-testid="landing-offer-badge">{offer.badge}</span>}
          <p className="kicker rise">{offer.kicker}</p>
          <h1 className="font-display rise d1 mt-5 text-5xl font-light leading-[1.05] text-neutral-950 sm:text-6xl" data-testid="landing-headline">{offer.line1}<br /><em>{offer.line2}</em></h1>
          <p className="rise d2 mt-7 max-w-md text-base font-light leading-relaxed text-neutral-600">{offer.sub}</p>
          <ul className="rise d3 mt-10 space-y-3 text-sm font-light text-neutral-800">
            {["5-star ratings from our clients", "Every price published below — no surprises", `Reserve with a ${DEPOSIT} deposit`, "Pay over time with Cherry financing"].map((t) => (
              <li key={t} className="flex items-center gap-3"><Check size={15} strokeWidth={1.25} /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="border border-neutral-200 p-8 md:p-12">
          {doneName ? <NextStep name={doneName} offer={offer} /> : <LeadForm onDone={setDoneName} offer={offer} />}
        </div>
      </section>

      <section className="bg-[hsl(var(--stone))] py-20 md:py-28" data-testid="landing-pricing">
        <div className="mx-auto grid max-w-[1200px] gap-16 px-5 md:px-8 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <p className="kicker">Full Transparency</p>
            <h2 className="font-display mt-3 text-4xl font-light text-neutral-950 md:text-5xl">Our pricing</h2>
            <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-neutral-600">Regular menu pricing. Your final plan is confirmed at consultation, and packages of three receive preferred pricing.</p>
            <div className="mt-8 flex flex-col items-start gap-4">
              <Link to="/treatments" className="link-under" data-testid="landing-full-menu-link">View the Full Menu</Link>
              <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="link-under" data-testid="landing-cherry-link">Apply for Cherry Financing</a>
            </div>
          </div>
          <dl className="divide-y divide-neutral-300 border-y border-neutral-300">
            {PRICES.map(([n, p]) => (
              <div key={n} className="flex items-baseline justify-between gap-6 py-4" data-testid="landing-price-row">
                <dt className="font-display text-xl text-neutral-950">{n}</dt>
                <dd className="text-right text-xs uppercase tracking-[0.15em] text-neutral-700">{p}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="grid lg:grid-cols-2">
        <div className="h-[420px] overflow-hidden lg:h-[560px]"><img src={IMG.museNeck} alt="Lux Veritae muse" className="h-full w-full object-cover object-top" /></div>
        <div className="flex flex-col justify-center px-6 py-16 md:px-16">
          <p className="kicker">How It Works</p>
          <ol className="mt-8 space-y-8">
            {[["Share your goals", "Two minutes, completely private."], [`Reserve with ${DEPOSIT}`, "Book your appointment or consultation online."], ["Your personalized plan", "Expert care designed around your skin and budget."]].map(([t, d], i) => (
              <li key={t} className="flex gap-6"><span className="font-display text-3xl text-neutral-400">0{i + 1}</span><div><p className="font-display text-2xl text-neutral-950">{t}</p><p className="mt-1 text-sm font-light text-neutral-600">{d}</p></div></li>
            ))}
          </ol>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-ink mt-12 self-start" data-testid="landing-back-to-form">Get Started</a>
        </div>
      </section>

      <footer className="bg-neutral-950 px-5 py-10 text-center text-[11px] tracking-[0.15em] text-neutral-400" data-testid="landing-footer">
        Lux Veritae Skin Spa · {ADDRESS_LINE1}, {ADDRESS_LINE2} · <a href={`tel:${PHONE_TEL}`} className="hover:text-white">{PHONE_DISPLAY}</a> · <Link to="/" className="hover:text-white">Visit our website</Link>
      </footer>
    </div>
  );
}
